import { Box, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useEffect, useState } from 'react';
import { DragDropFileUploadProps } from './DragDropFileUploadProps';

const DragDropFileUpload = ({
  acceptedFileTypes,
  onFileSelected,
  maxFileSizeMB,
  label = 'Drag and drop file here',
  sublabel = 'or click to browse',
}: DragDropFileUploadProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');

  const validateFile = (file: File): boolean => {
    setError('');

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = acceptedFileTypes.some(type => 
      type.toLowerCase() === fileExtension || 
      file.type === type,
    );

    if (!isValidType) {
      setError(`Invalid file type. Accepted types: ${acceptedFileTypes.join(', ')}`);
      return false;
    }

    if (maxFileSizeMB && file.size > maxFileSizeMB * 1024 * 1024) {
      setError(`File size exceeds ${maxFileSizeMB}MB limit`);
      return false;
    }

    return true;
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        onFileSelected(file);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        onFileSelected(file);
      }
    }
  };

  const inputId = `file-upload-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <Box>
      <Box
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          p: 4,
          border: `2px dashed ${error ? '#d32f2f' : dragActive ? 'black' : '#ccc'}`,
          borderRadius: 2,
          backgroundColor: error ? '#ffebee' : dragActive ? '#f0f7ff' : '#fafafa',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: error ? '#d32f2f' : 'black',
            backgroundColor: error ? '#ffebee' : '#f0f7ff',
          },
        }}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <input
          id={inputId}
          type="file"
          accept={acceptedFileTypes.join(',')}
          onChange={handleChange}
          style={{ display: 'none' }}
        />
        <UploadFileIcon sx={{ fontSize: 48, color: error ? '#d32f2f' : 'black', mb: 2 }} />
        <Typography
          gutterBottom>
          {selectedFile ? selectedFile.name : label}
        </Typography>
        {!selectedFile?.name &&
          <Typography
            color="textSecondary">
            {sublabel}
          </Typography>}
      </Box>
      {error && (
        <Typography
          color="error" 
          sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DragDropFileUpload;